import { HTML5Configuration } from 'Configuration/accessors/HTML5ConfigAccessor';
import { ChromeAppMessage } from 'Environment/ChromeApp/types';

const INIT_EVENT = 'workspace/cvad/html5/initialised' as const;
const INIT_EVENT_ACK = 'workspace/cvad/html5/initialisedAck' as const;
// "init" event needs cleanup later as new "initialised" event will be used
const INITIALIZE_EVENT = 'workspace/cvad/html5launch/icadownload/init' as const;
const SUCCESS_EVENT = 'workspace/cvad/html5launch/icadownload/success' as const;
const ERROR_EVENT = 'workspace/cvad/html5launch/icadownload/error' as const;
const RETRY_EVENT = 'workspace/cvad/html5launch/icadownload/retry' as const;
const INIT_MESSAGE_TIMEOUT_MS = 10000;

interface PlatformInfo {
	wsuiVersion: string;
	buildVersion: string;
}
export interface WSUICapabilities {
	apiversion: string;
	platform: PlatformInfo;
	capabilities: HTML5Configuration;
}

type BaseEvent = {
	type: string;
	payload: unknown;
};

type SuccessEvent = {
	type: typeof SUCCESS_EVENT;
	payload: {
		icaData: object;
	};
};

type ErrorEvent = {
	type: typeof ERROR_EVENT;
	payload: {
		errorMessage: string;
	};
};

type RetryEvent = {
	type: typeof RETRY_EVENT;
	payload: null;
};

type InitAckEvent = {
	header: {
		type: typeof INIT_EVENT_ACK;
	};
	payload: {
		correlationId: string;
		apiversion: string;
		capabilities: HTML5Configuration;
		platform: PlatformInfo;
		[key: string]: any;
	};
};

type InitEvent = {
	header: {
		type: typeof INIT_EVENT;
	};
	payload: {
		correlationId: string;
		[key: string]: any;
	};
};

type InitializeEvent = {
	type: typeof INITIALIZE_EVENT;
	payload: {
		version: number;
	};
};

type SentEvents = SuccessEvent | ErrorEvent | RetryEvent | InitAckEvent;

export class HTML5Client {
	private usePostMessage = false;
	private errorId: string;
	private initMessageListener: ((event: MessageEvent) => void) | null = null;
	private initTimeoutId: NodeJS.Timeout | null = null;

	public constructor(private readonly targetWindow: Window & Record<string, any>) {}

	public startListening(wsuiCapabilities: WSUICapabilities) {
		if (!wsuiCapabilities.capabilities.supportsHTML5InitialisedEvent) {
			window.addEventListener('message', this.onMessage);
			return Promise.resolve(null);
		} else {
			return this.setupInitMessageHandling(wsuiCapabilities);
		}
	}

	public stopListening() {
		window.removeEventListener('message', this.onMessage);
		// Also clean up init message listener if it exists
		if (this.initMessageListener) {
			window.removeEventListener('message', this.initMessageListener);
			this.initMessageListener = null;
		}
		// Clear timeout if it exists
		if (this.initTimeoutId) {
			clearTimeout(this.initTimeoutId);
			this.initTimeoutId = null;
		}
	}

	public sendSuccess(icaData: object, launchId: number) {
		if (this.usePostMessage) {
			this.sendEvent({
				type: SUCCESS_EVENT,
				payload: { icaData },
			});
		} else {
			window.html5LaunchData ??= {};
			window.html5LaunchData[launchId] = icaData;
			if (this.targetWindow) {
				this.targetWindow.icaDwldComplete = true;
			}
		}
	}

	/**
	 * Errors can be triggered in a few different places in the launch flow, but an explicit
	 * error id should be captured as soon as it's known for maximum accuracy -- the error
	 * may be wrapped up and thrown later. The error id is passed to the html5 client so
	 * it can be surfaced in collected logs.
	 */
	public setErrorId(errorId: string) {
		this.errorId = errorId;
	}

	public sendError(error: unknown) {
		if (this.usePostMessage) {
			let errorMessage: string;
			if (this.errorId) {
				errorMessage = this.errorId;
			} else if (error instanceof Error) {
				errorMessage = `${error.name}: ${error.message}`;
			} else {
				errorMessage = 'ICADownloadError';
			}
			this.sendEvent({
				type: ERROR_EVENT,
				payload: { errorMessage },
			});
		} else {
			this.targetWindow?.close();
		}
	}

	/**
	 * With a retry event we can notify the HTML5 client that the cloud broker is online
	 * and responding, but that the VDA needs more time to spin up. The HTML5 client
	 * should then cancel its own launch flow timeout and delegate to the broker's
	 * timeout, which is configured by the customer's admin. This does not _completely_
	 * remove timeouts, as WSP still enforces an internal 60-second timeout for each
	 * outgoing call. After a retry, it's assumed an eventual error or success will
	 * follow
	 */
	public sendRetry() {
		if (this.usePostMessage) {
			this.sendEvent({
				type: RETRY_EVENT,
				payload: null,
			});
		}
	}

	private onMessage = (message: unknown) => {
		if (this.isInitializeEventFromLaunchWindow(message)) {
			this.usePostMessage = true;
		}
	};

	private isInitializeEventFromLaunchWindow(
		event: unknown
	): event is MessageEvent<InitializeEvent> {
		if (event instanceof MessageEvent && event.origin === location.origin) {
			const { data } = event;
			return (
				data?.type === INITIALIZE_EVENT && typeof data?.payload?.version === 'number'
			);
		}

		return false;
	}

	public sendEvent<E extends (BaseEvent & SentEvents) | ChromeAppMessage>(event: E) {
		this.targetWindow.postMessage(event, location.origin);
	}

	/**
	 * Validates whether an event is part of the HTML5 initialization handshake.
	 * This functionality is controlled by the feature flag: WSUI11394EnableHTML5InitialisedEvent
	 * Both GACS v2 and session reconnect features require this event handling to be enabled.
	 */
	private isInitializeEvent(event: unknown): event is MessageEvent<InitEvent> {
		if (event instanceof MessageEvent && event.origin === location.origin) {
			const { data } = event;
			return (
				data?.header?.type === INIT_EVENT &&
				typeof data?.payload?.correlationId === 'string'
			);
		}

		return false;
	}

	/**
	 * Sets up init message handling with timeout
	 * Returns a promise that resolves when init message is received or times out
	 */
	public setupInitMessageHandling(wsuiCapabilities: WSUICapabilities): Promise<any> {
		return new Promise<any>(resolve => {
			this.initMessageListener = (event: MessageEvent) => {
				// Early return if not from the expected target window
				if (event.source !== this.targetWindow) {
					return;
				}

				// Check if this is an InitializeEventV2
				if (!this.isInitializeEvent(event)) {
					return;
				}

				const ackEvent: InitAckEvent = {
					header: {
						type: INIT_EVENT_ACK,
					},
					payload: {
						correlationId: event.data.payload?.correlationId,
						apiversion: wsuiCapabilities?.apiversion,
						capabilities: wsuiCapabilities?.capabilities,
						platform: {
							wsuiVersion: wsuiCapabilities?.platform?.wsuiVersion,
							buildVersion: wsuiCapabilities?.platform?.buildVersion,
						},
					},
				};

				this.sendEvent(ackEvent); // Acknowledge receipt with WSUI capabilities
				this.usePostMessage = true;
				resolve(event.data);
			};

			// Start listening for init message immediately
			window.addEventListener('message', this.initMessageListener);

			// Set up timeout - continue launch even if init message doesn't arrive
			this.initTimeoutId = setTimeout(() => {
				resolve(null); // Resolve with null instead of rejecting
			}, INIT_MESSAGE_TIMEOUT_MS); // 10 second timeout
		});
	}
}
